<script lang="ts">
  import { computed, defineComponent, h, type PropType } from 'vue';
  import type { Element, Root } from 'hast';
  import {
    htmlToVNodes,
    hastPropertiesToAttrs,
    type HtmlVNodeChildren,
    type ReplaceResult,
  } from '@nuxt-shared/lib/html-to-vnodes';
  import {
    createShortcodeReplacer,
    type ShortcodeMap,
  } from '@nuxt-shared/lib/cms-shortcodes';
  import LazyIframeVideo from '@nuxt-shared/ui/lazy-iframe-video/LazyIframeVideo.vue';
  import {
    applyElementReplacers,
    type CmsContentImageRenderer,
    type CmsElementReplacer,
  } from './types';

  /** Класс обёртки-скроллера вокруг широкой таблицы (стили — на стороне страницы). */
  const SCROLL_WRAP_CLASS = 'scroll-wrap';

  function hasClass(el: Element, name: string): boolean {
    const cls = el.properties?.className;
    if (Array.isArray(cls)) return cls.includes(name);
    return String(cls ?? '')
      .split(/\s+/)
      .includes(name);
  }

  // Обход всего поддерева, а не только прямых детей: редактор оборачивает
  // вставку в <p>/<div>, а страницы иногда сами добавляют свой .video-frame
  // поверх редакторского — при поиске на один уровень iframe не находился и
  // плеер молча рендерился как обычный HTML, без ленивой загрузки.
  function collectIframes(el: Element): Element[] {
    const result: Element[] = [];

    for (const child of el.children) {
      if (child.type !== 'element') continue;
      if (child.tagName === 'iframe') result.push(child);
      else result.push(...collectIframes(child));
    }

    return result;
  }

  /** Единственное изображение в абзаце редактора, не считая пробелов и `<br>`. */
  function singleParagraphImage(el: Element): Element | null {
    let image: Element | null = null;

    for (const child of el.children) {
      if (child.type === 'text' && !child.value.trim()) continue;
      if (child.type === 'element' && child.tagName === 'br') continue;
      if (child.type !== 'element' || child.tagName !== 'img' || image) return null;
      image = child;
    }

    return image;
  }

  /**
   * Текст абзаца, если внутри только текст и переносы строк. `null` — в абзаце
   * есть разметка, трогать его нельзя.
   */
  function plainParagraphText(el: Element): string | null {
    let text = '';

    for (const child of el.children) {
      if (child.type === 'text') text += child.value;
      else if (child.type !== 'element' || child.tagName !== 'br') return null;
    }

    return text;
  }

  /**
   * Рендерит HTML из CMS (Битрикс-редактор) как VNode-дерево вместо v-html —
   * это позволяет подставлять настоящие Vue-компоненты прямо в текст:
   *
   * - `<div class="video-frame"><iframe src=…></div>` (редакторская вставка
   *   видео) → `LazyIframeVideo`: на SSR — карточка-фасад с постером, iframe
   *   плеера вставляется по клику, его JS не грузится со страницей. Данные
   *   постера редактор задаёт data-атрибутами прямо на iframe:
   *   `data-title`, `data-description`, `data-duration`, `data-poster`;
   * - `<img>` → пользовательский компонент через `imageRenderer` либо, без
   *   него, `loading="lazy" decoding="async"`;
   * - прочие `<iframe>` без `loading` (виджеты, не-видео) → `loading="lazy"`;
   * - `<table>` → обёрнута в `<div class="scroll-wrap">`, если включён
   *   `wrapTables`;
   * - шорткоды из `shortcodes` (`#accordion_2-5#`, `#gallery#`, …) → компоненты
   *   на их место. Абзац, в котором кроме шорткода ничего нет, снимается:
   *   блочный компонент внутри `<p>` браузер вынес бы наружу и SSR-разметка
   *   разошлась бы с клиентской.
   *
   * Атрибуты (class и т.п.) прокидываются на корневой div.
   */
  export default defineComponent({
    name: 'CmsContent',
    props: {
      html: { type: String, default: '' },
      videoPriority: { type: Boolean, default: false },
      wrapTables: { type: Boolean, default: false },
      imageRenderer: {
        type: Function as PropType<CmsContentImageRenderer>,
        default: undefined,
      },
      shortcodes: {
        type: Object as PropType<ShortcodeMap>,
        default: undefined,
      },
      elements: {
        type: Array as PropType<CmsElementReplacer[]>,
        default: undefined,
      },
    },
    setup(props) {
      const shortcodeReplacer = computed(() =>
        createShortcodeReplacer(props.shortcodes || {}),
      );

      const nodes = computed(() => {
        // Приоритет достаётся только первому плееру этого контента — счётчик
        // живёт внутри computed, чтобы сбрасываться на каждом пересчёте.
        let videoCount = 0;

        // Редактор иногда сам обрамляет таблицу `.scroll-wrap` — второй раз не
        // оборачиваем. Обход синхронный и в глубину, поэтому флага достаточно.
        let insideScrollWrap = false;

        const replacer = shortcodeReplacer.value;

        const replaceElement = (
          el: Element,
          renderChildren: () => HtmlVNodeChildren,
          parent?: Element | Root,
        ): ReplaceResult => {
          // Правила страницы идут первыми: блок, размеченный data-атрибутами
          // (например `<div data-charts>` конструктора графиков), должен стать
          // компонентом раньше, чем сработают встроенные правила.
          const byRule = applyElementReplacers(props.elements ?? [], el, renderChildren);

          if (byRule !== undefined) return byRule;

          if (props.imageRenderer && el.tagName === 'p') {
            const image = singleParagraphImage(el);
            const rendered = image ? props.imageRenderer(image) : undefined;
            if (rendered !== undefined) return rendered;
          }

          // Абзац целиком из шорткодов — рендерим их вместо него, а пробелы,
          // на которых абзац и держался, выбрасываем.
          if (replacer && el.tagName === 'p') {
            const text = plainParagraphText(el);
            if (text !== null && replacer.isShortcodeOnlyText(text)) {
              const rendered = replacer.replaceText(text);
              if (rendered !== undefined) {
                const children = Array.isArray(rendered) ? rendered : [rendered];
                return children.filter((child) => typeof child !== 'string');
              }
            }
          }

          if (el.tagName === 'div' && hasClass(el, 'video-frame')) {
            const iframes = collectIframes(el);
            const src = iframes.length === 1 ? iframes[0]?.properties?.src : undefined;
            if (typeof src === 'string' && src) {
              const attrs = iframes[0]?.properties ?? {};
              const str = (val: unknown) => (typeof val === 'string' ? val : '');
              const isFirstVideo = videoCount++ === 0;
              return h(LazyIframeVideo, {
                src,
                title: str(attrs.dataTitle) || str(attrs.title),
                description: str(attrs.dataDescription),
                duration: str(attrs.dataDuration),
                poster: str(attrs.dataPoster),
                priority: props.videoPriority && isFirstVideo,
              });
            }
            // iframe не нашли/не одинок или без src — обычный рендер обёртки.
            return undefined;
          }

          if (el.tagName === 'img') {
            // Блочный компонент нельзя помещать внутрь текстового `<p>`.
            // Абзац с единственной картинкой уже обработан выше; смешанный
            // текст оставляем обычным HTML.
            const rendered =
              parent?.type !== 'element' || parent.tagName !== 'p'
                ? props.imageRenderer?.(el)
                : undefined;
            if (rendered !== undefined) return rendered;

            if (el.properties?.loading == null) {
              return h('img', {
                ...hastPropertiesToAttrs(el.properties),
                loading: 'lazy',
                decoding: 'async',
              });
            }
          }

          if (el.tagName === 'iframe' && el.properties?.loading == null) {
            return h('iframe', {
              ...hastPropertiesToAttrs(el.properties),
              loading: 'lazy',
            });
          }

          if (props.wrapTables) {
            if (el.tagName === 'div' && hasClass(el, SCROLL_WRAP_CLASS)) {
              const wasInside = insideScrollWrap;
              insideScrollWrap = true;
              const children = renderChildren();
              insideScrollWrap = wasInside;

              return h(
                'div',
                hastPropertiesToAttrs(el.properties),
                children.length ? children : undefined,
              );
            }

            if (el.tagName === 'table' && !insideScrollWrap) {
              return h('div', { class: SCROLL_WRAP_CLASS }, [
                h('table', hastPropertiesToAttrs(el.properties), renderChildren()),
              ]);
            }
          }

          return undefined;
        };

        return htmlToVNodes(props.html, {
          element: replaceElement,
          text: replacer?.replaceText,
        });
      });

      return () => (nodes.value.length ? h('div', nodes.value) : null);
    },
  });
</script>
